import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';
import { setFlashToast } from '../../utils/flashToast';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import {
  completeSubsidiaryTask,
  listAssignedUserSubsidiaryTasks,
  startSubsidiaryTask,
  type AssignedUserSubsidiaryTaskRow,
} from '../../services/caseRequestSubsidiaryTasks';
import type { TaskCaseSubTasksTabProps } from './taskCaseSubTasksTypes';

type LoadState = 'idle' | 'loading' | 'error';

type JuniorSubTaskCardModel = AssignedUserSubsidiaryTaskRow;

function isPendingStatus(status: string): boolean {
  const s = status.trim().toLowerCase();
  return !s || s.includes('pending') || s.includes('not started');
}

function isInProgressStatus(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s.includes('progress') || s.includes('started');
}

function isCompletedStatus(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s.includes('complete') || s.includes('closed') || s.includes('done');
}

type SubTaskCardProps = {
  task: JuniorSubTaskCardModel;
  readOnly: boolean;
  onReload: () => Promise<void>;
};

function SubTaskCard({ task, readOnly, onReload }: SubTaskCardProps) {
  const pending = isPendingStatus(task.status);
  const inProgress = isInProgressStatus(task.status);
  const completed = isCompletedStatus(task.status);

  const [comment, setComment] = useState(task.comment ?? '');
  const [commentError, setCommentError] = useState('');
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    setComment(task.comment ?? '');
    setCommentError('');
  }, [task.subsidiaryTaskId, task.comment, task.status]);

  const commentDisabled = readOnly || completed || pending;

  const hasUnsavedComment = comment !== (task.comment ?? '');

  const resetComment = () => {
    setComment(task.comment ?? '');
    setCommentError('');
  };

  const handleCancelClick = () => {
    if (hasUnsavedComment) {
      setCancelConfirmOpen(true);
      return;
    }
    resetComment();
  };

  const handleStart = async () => {
    if (readOnly || starting || !task.showStartButton) return;
    setStarting(true);
    try {
      const res = await startSubsidiaryTask({ subsidiaryTaskId: task.subsidiaryTaskId });
      if (!res.success) {
        setFlashToast({ message: res.message ?? 'Failed to start subsidiary task.', variant: 'error' });
        return;
      }
      setFlashToast({ message: 'Subsidiary task started.', variant: 'success' });
      await onReload();
    } finally {
      setStarting(false);
    }
  };

  const handleComplete = async () => {
    if (readOnly || completing || !task.showMarkCompleteButton) return;
    const trimmed = comment.trim();
    if (!trimmed) {
      setCommentError('Comment is required to mark the task as completed.');
      return;
    }
    setCommentError('');
    setCompleting(true);
    try {
      const res = await completeSubsidiaryTask({ subsidiaryTaskId: task.subsidiaryTaskId, comment: trimmed });
      if (!res.success) {
        setFlashToast({ message: res.message ?? 'Failed to complete subsidiary task.', variant: 'error' });
        return;
      }
      setFlashToast({ message: 'Subsidiary task marked as completed.', variant: 'success' });
      await onReload();
    } finally {
      setCompleting(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          resetComment();
          setCancelConfirmOpen(false);
        }}
      />

      <article className="tcd-junior-subtask-card" aria-labelledby={`subtask-title-${task.subsidiaryTaskId}`}>
      <div className="tcd-junior-subtask-card-head">
        <h3 className="tcd-junior-subtask-card-title" id={`subtask-title-${task.subsidiaryTaskId}`}>
          {task.subsidiaryTask || '—'}
        </h3>
        {task.showStartButton && !readOnly ? (
          <button
            type="button"
            className="tcd-junior-subtask-start-btn"
            onClick={() => void handleStart()}
            disabled={starting}
          >
            {starting ? 'Starting…' : 'Start'}
          </button>
        ) : null}
        {inProgress ? <span className="tcd-junior-subtask-started-badge">Started</span> : null}
        {completed ? <span className="tcd-junior-subtask-started-badge is-complete">Completed</span> : null}
      </div>

      {task.description ? <p className="tcd-junior-subtask-card-desc">{task.description}</p> : null}

      <div className="tcd-junior-subtask-card-meta">
        {task.dueDate ? (
          <div className="tcd-junior-subtask-meta-item">
            <span className="tcd-junior-subtask-meta-label">Due Date</span>
            <span className="tcd-junior-subtask-meta-value is-due">{formatDisplayDate(task.dueDate)}</span>
          </div>
        ) : null}
        <div className="tcd-junior-subtask-meta-item">
          <span className="tcd-junior-subtask-meta-label">Workflow Stage</span>
          <span className="tcd-junior-subtask-meta-value is-stage">{task.workflowStage || '—'}</span>
        </div>
        {completed && task.completedAt ? (
          <div className="tcd-junior-subtask-meta-item">
            <span className="tcd-junior-subtask-meta-label">Completed At</span>
            <span className="tcd-junior-subtask-meta-value is-completed-at">
              {formatDisplayDateTime(task.completedAt)}
            </span>
          </div>
        ) : null}
      </div>

      {completed && task.comment?.trim() ? (
        <div className="tcd-junior-subtask-completed-comment">
          <span className="tcd-junior-subtask-meta-label">Comment</span>
          <p className="tcd-junior-subtask-completed-comment-text">{task.comment.trim()}</p>
        </div>
      ) : null}

      {inProgress ? (
        <div className="tcd-junior-subtask-comment-wrap">
          <label className="tcd-junior-subtask-comment-label" htmlFor={`subtask-comment-${task.subsidiaryTaskId}`}>
            Comment <span className="tcd-junior-subtask-required">*</span>
          </label>
          <textarea
            id={`subtask-comment-${task.subsidiaryTaskId}`}
            className="ccr-inp tcd-junior-subtask-comment"
            rows={4}
            maxLength={2000}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (commentError) setCommentError('');
            }}
            placeholder="Add any completion notes or remarks..."
            disabled={commentDisabled}
            aria-invalid={Boolean(commentError)}
            aria-required
          />
          {commentError ? (
            <p className="tcd-junior-subtask-comment-error" role="alert">
              {commentError}
            </p>
          ) : null}
        </div>
      ) : null}

      {task.showMarkCompleteButton && !readOnly ? (
        <div className="tcd-junior-subtask-card-foot">
          <button type="button" className="ccr-btn-ol" onClick={handleCancelClick} disabled={completing}>
            Cancel
          </button>
          <button type="button" className="ccr-btn-pr" onClick={() => void handleComplete()} disabled={completing}>
            {completing ? 'Saving…' : 'Mark as Completed'}
          </button>
        </div>
      ) : null}
    </article>
    </>
  );
}

export default function TaskCaseJuniorSubTasksTab({
  caseRequestId,
  readOnly = false,
}: Pick<TaskCaseSubTasksTabProps, 'caseRequestId' | 'workflowStage' | 'readOnly'>) {
  const { t } = useTranslation();
  const useLiveApi = isCaseRequestUuid(caseRequestId);

  const [tasksState, setTasksState] = useState<LoadState>(useLiveApi ? 'loading' : 'idle');
  const [tasks, setTasks] = useState<JuniorSubTaskCardModel[]>([]);

  const reloadTasks = async (signal?: AbortSignal) => {
    if (!useLiveApi) return;
    setTasksState('loading');
    const res = await listAssignedUserSubsidiaryTasks({ caseRequestId, signal });
    if (signal?.aborted) return;
    if (!res.success) {
      setTasksState('error');
      return;
    }
    setTasks(res.data.tasks);
    setTasksState('idle');
  };

  useEffect(() => {
    if (!useLiveApi) return;
    const controller = new AbortController();
    void reloadTasks(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useLiveApi, caseRequestId]);

  const displayTasks = tasks;

  if (!useLiveApi) {
    return (
      <div className="tcd-junior-subtasks">
        <div className="log-title">Sub Tasks</div>
        <p className="tcd-doc-empty-msg">Case request id is missing or invalid.</p>
      </div>
    );
  }

  return (
    <div className="tcd-junior-subtasks">
      <div className="log-title">Sub Tasks</div>
      <h2 className="tcd-junior-subtasks-section-title">Subsidiary Tasks</h2>
      <p className="tcd-junior-subtasks-subtitle">Assigned Subsidiary Task(s)</p>

      {useLiveApi && tasksState === 'error' ? (
        <p className="tcd-update-ts" style={{ color: 'var(--red)', padding: '10px 0' }} role="alert">
          Failed to load subsidiary tasks.
        </p>
      ) : null}

      {useLiveApi && tasksState === 'loading' ? (
        <p className="tcd-update-ts" style={{ padding: '12px 0', color: 'var(--t2)' }}>
          Loading…
        </p>
      ) : displayTasks.length ? (
        <div className="tcd-junior-subtask-cards">
          {displayTasks.map((task) => (
            <SubTaskCard
              key={task.subsidiaryTaskId}
              task={task}
              readOnly={readOnly || !useLiveApi}
              onReload={() => reloadTasks()}
            />
          ))}
        </div>
      ) : tasksState !== 'loading' ? (
        <p className="tcd-junior-subtasks-empty">{t('common.noSubsidiaryTasksAssigned')}</p>
      ) : null}
    </div>
  );
}
